<template>
  <section aria-labelledby="admin-support-title">
    <div class="support-toolbar">
      <h2
        id="admin-support-title"
        class="text-h5"
      >
        {{ $t("\n        Support messages\n      ") }}
      </h2>
      <v-select
        v-model="status"
        :items="statuses"
        item-text="text"
        item-value="value"
        :label="$t(&quot;Status&quot;)"
        outlined
        dense
        hide-details
        clearable
        @change="filterChanged"
      />
      <v-btn
        outlined
        :loading="loading"
        @click="load"
      >
        {{ $t("\n        Refresh\n      ") }}
      </v-btn>
    </div>
    <p class="text-caption mt-3">
      {{ $t("\n      Privacy and security requests appear first. Dates use your local time.\n    ") }}
    </p>
    <v-alert
      v-if="error"
      type="error"
      text
    >
      {{ error }}
    </v-alert>
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :server-items-length="total"
      :page.sync="page"
      :items-per-page="20"
      :footer-props="{ 'items-per-page-options': [20] }"
      @update:page="load"
    >
      <template v-slot:item.created_at="{ item }">
        {{ date(item.created_at) }}
      </template>
      <template v-slot:item.status="{ item }">
        {{ statusText(item.status) }}
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
          small
          text
          color="primary"
          @click="open(item.id)"
        >
          {{ $t("\n          View request\n        ") }}
        </v-btn>
      </template>
      <template v-slot:no-data>
        {{ $t("\n        No support messages found.\n      ") }}
      </template>
    </v-data-table>
    <v-dialog
      v-model="dialog"
      max-width="760"
      :persistent="saving"
    >
      <v-card>
        <v-card-title>{{ $t("Support request") }}</v-card-title>
        <v-card-text class="pt-3">
          <v-progress-linear
            v-if="detailLoading"
            indeterminate
          />
          <v-alert
            v-if="detailError"
            type="error"
            text
          >
            {{ detailError }}
          </v-alert>
          <template v-if="selected">
            <h3 class="mb-3">
              {{ selected.topic }}
            </h3>
            <p><strong>{{ selected.name }}</strong><br />{{ selected.email }}</p>
            <p class="text-caption">
              {{ $t("\n              Reference: ") }}{{ selected.id }}<br />{{ $t("Received: ") }}{{ date(selected.created_at) }}<br />{{ $t("Email copy: ") }}{{ selected.email_status }}
            </p>
            <p v-if="selected.device">
              {{ $t("\n              Device/browser: ") }}{{ selected.device }}
            </p>
            <div class="support-message-body">
              {{ selected.message }}
            </div>
            <v-select
              v-model="nextStatus"
              class="mt-5"
              :items="statuses"
              :label="$t(&quot;Request status&quot;)"
              outlined
              :disabled="saving"
            />
            <v-btn
              color="primary"
              :loading="saving"
              :disabled="nextStatus === selected.status"
              @click="save"
            >
              {{ $t("\n              Save status\n            ") }}
            </v-btn>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer /><v-btn
            text
            :disabled="saving"
            @click="dialog = false"
          >
            {{ $t("\n            Close\n          ") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>
<script>
import { t } from '@/i18n'

import support from '@/services/support'

export default {
  name: 'AdminSupport',
  data: () => ({
    items: [],
    total: 0,
    page: 1,
    status: null,
    loading: false,
    error: '',
    sequence: 0,
    dialog: false,
    selected: null,
    nextStatus: '',
    saving: false,
    detailLoading: false,
    detailError: '',
    statuses: [{ get text() { return t('Open') }, value: 'open' }, { get text() { return t('In progress') }, value: 'in_progress' }, { get text() { return t('Resolved') }, value: 'resolved' }],
    headers: [
      { get text() { return t('Received') }, value: 'created_at', sortable: false }, { get text() { return t('Topic') }, value: 'topic', sortable: false },
      { get text() { return t('Name') }, value: 'name', sortable: false }, { get text() { return t('Email') }, value: 'email', sortable: false },
      { get text() { return t('Status') }, value: 'status', sortable: false }, { get text() { return t('Email copy') }, value: 'email_status', sortable: false },
      { text: '', value: 'actions', sortable: false },
    ],
  }),
  mounted() { this.load() },
  methods: {
    date(value) { return value ? new Date(/Z$/.test(value) ? value : `${value.replace(' ', 'T')}Z`).toLocaleString() : '' },
    statusText(value) {
      const match = this.statuses.find(item => item.value === value)

      return match ? match.text : value
    },
    filterChanged() { this.page = 1; this.load() },
    async load() {
      this.sequence += 1
      const { sequence } = this
      this.loading = true
      this.error = ''
      try {
        const result = await support.list({ page: this.page, status: this.status || undefined })
        if (sequence === this.sequence) { this.items = result.items; this.total = result.total }
      } catch (error) {
        if (sequence === this.sequence) this.error = (error.response && error.response.data && error.response.data.message) || t('Unable to load support messages.')
      } finally { if (sequence === this.sequence) this.loading = false }
    },
    async open(id) {
      this.dialog = true
      this.selected = null
      this.detailError = ''
      this.detailLoading = true
      try { this.selected = await support.detail(id); this.nextStatus = this.selected.status } catch (error) { this.detailError = (error.response && error.response.data && error.response.data.message) || t('Unable to load this request.') } finally { this.detailLoading = false }
    },
    async save() {
      this.saving = true
      this.detailError = ''
      try { this.selected = await support.update(this.selected.id, this.nextStatus); await this.load() } catch (error) { this.detailError = (error.response && error.response.data && error.response.data.message) || t('Unable to save the status.') } finally { this.saving = false }
    },
  },
}
</script>
<style scoped>
.support-toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 16px; }
.support-toolbar h2 { flex: 1 1 240px; }
.support-toolbar .v-input { flex: 0 1 200px; }
.support-message-body { white-space: pre-wrap; overflow-wrap: anywhere; border: 1px solid rgba(128,128,128,.25); border-radius: 6px; padding: 16px; }
</style>
